import {
  BoundNumberField,
  BoundTextField,
  Button,
  Css,
  GridDataRow,
  GridTable,
  IconButton,
  ScrollableContent,
  actionColumn,
  column,
  emptyCell,
  simpleHeader,
  useModal,
} from "@homebound/beam";
import { ObjectConfig, ObjectState, useFormStates } from "@homebound/form-state";
import { useCallback, useMemo, useState } from "react";
import { SearchBox } from "src/components";
import { useFeatureFlag } from "src/contexts/FeatureFlags/FeatureFlagContext";
import { FeatureFlagType, SaveGlobalPlanConstraintItemInput } from "src/generated/graphqlTypes";
import { PageHeader } from "src/routes/layout/PageHeader";
import { TableActions } from "src/routes/layout/TableActions";
import { useSaveGlobalPlanConstraintItemMutation } from "src/routes/libraries/task-catalog/components/AddConstraintItemModal.generated";
import {
  ConstraintAndAllowanceCatalogPage_GlobalPlanConstraintItemFragment,
  useConstraintAndAllowanceCatalogPageQuery,
  useDeleteGlobalPlanConstraintItemMutation,
} from "src/routes/libraries/task-catalog/ConstraintItemCatalogPage.generated";
import { catalogTasksPath } from "src/routes/routesDef";
import { AddConstraintItemModal, ConstraintModalConfig } from "./components/AddConstraintItemModal";

export function ConstraintItemCatalogPage() {
  const { data } = useConstraintAndAllowanceCatalogPageQuery();
  const { openModal } = useModal();
  const [search, setSearch] = useState<string | undefined>();
  const [saveGlobalPlanConstraintItem] = useSaveGlobalPlanConstraintItemMutation();
  const [deleteGlobalPlanConstraintItem] = useDeleteGlobalPlanConstraintItemMutation();
  const isReadonly = !useFeatureFlag(FeatureFlagType.TaskCatalogAdmin);

  const { getFormState } = useFormStates<
    SaveGlobalPlanConstraintItemInput,
    ConstraintAndAllowanceCatalogPage_GlobalPlanConstraintItemFragment
  >({
    config: formConfig,
    readOnly: isReadonly,
    getId: (v) => v.id,
    autoSave: async (os) => {
      await saveGlobalPlanConstraintItem({ variables: { input: os.changedValue } });
      os.commitChanges();
    },
  });

  const onDelete = useCallback(
    async (id: string) => {
      await deleteGlobalPlanConstraintItem({
        variables: { input: { id } },
        update: (cache) => cache.evict({ id: cache.identify({ __typename: "GlobalPlanConstraintItem", id }) }),
      });
    },
    [deleteGlobalPlanConstraintItem],
  );

  const columns = useMemo(
    () => createColumns(getFormState, onDelete, isReadonly),
    [getFormState, onDelete, isReadonly],
  );
  const rows = useMemo(() => createRows(data?.globalPlanConstraintItems ?? []), [data]);

  return (
    <div>
      <PageHeader
        breadcrumb={[{ href: catalogTasksPath, label: "Task Catalog" }]}
        title="Constraint/ Allowance Catalog"
        right={
          !isReadonly && (
            <Button
              label="Create"
              onClick={() =>
                openModal({ content: <AddConstraintItemModal constraintType={ConstraintModalConfig.ConstraintItem} /> })
              }
            />
          )
        }
      />
      <div>
        <TableActions>
          <p>
            Constraints and allowances share the same items, but can be used as buffers either before or after a task.
          </p>
          <div css={Css.df.jcsb.$}>
            <SearchBox onSearch={setSearch} />
            {/* ToDo: Implement in another ticket */}
            {/* <ButtonMenu trigger={{ icon: "verticalDots" }} items={[{ label: "Delete", onClick: noop }]} /> */}
          </div>
        </TableActions>
      </div>
      <ScrollableContent virtualized>
        <GridTable
          columns={columns}
          rows={rows}
          as="virtual"
          filter={search}
          rowStyles={{ data: { cellCss: Css.px3.$ } }}
        />
      </ScrollableContent>
    </div>
  );
}

type HeaderRow = { kind: "header" };
type DataRow = { kind: "data"; data: ConstraintAndAllowanceCatalogPage_GlobalPlanConstraintItemFragment };

export type Row = HeaderRow | DataRow;

function createColumns(
  getFormState: (
    input: ConstraintAndAllowanceCatalogPage_GlobalPlanConstraintItemFragment,
  ) => ObjectState<SaveGlobalPlanConstraintItemInput>,
  onDelete: (id: string) => void,
  isReadonly: boolean,
) {
  return [
    column<Row>({
      header: "Constraint/Allowance Name",
      data: (row) => ({
        content: () => {
          const os = getFormState(row);
          return <BoundTextField label="ConstraintOrAllowanceName" field={os.name} />;
        },
        value: () => row.name,
      }),
      w: "50%",
    }),
    column<Row>({
      header: "Lag / Lead",
      data: (row) => {
        const os = getFormState(row);

        // readonly version of the boundnumberfield looks terrible in the table, so adding a simple span instead
        if (isReadonly) {
          return <span css={Css.sm.$}>{os.lagInDays.value}</span>;
        } else {
          return <BoundNumberField label="lagInDays" type="days" field={os.lagInDays} />;
        }
      },
    }),
    column<Row>({
      header: "Times Used",
      data: ({ numberOfTimesUsed }) => <span css={Css.sm.$}>{numberOfTimesUsed}</span>,
    }),
    // only current action is delete, so only include action column if not readonly
    ...(!isReadonly
      ? [
          actionColumn<Row>({
            header: emptyCell,
            data: (row) => (
              <IconButton
                icon="trash"
                disabled={!row.canDelete.allowed}
                tooltip={row.canDelete.disabledReasons.map((r) => r.message).join(", ") || undefined}
                onClick={() => onDelete(row.id)}
              />
            ),
            w: "50px",
          }),
        ]
      : []),
  ].compact();
}

function createRows(data: ConstraintAndAllowanceCatalogPage_GlobalPlanConstraintItemFragment[]): GridDataRow<Row>[] {
  return [simpleHeader, ...data?.map((gpci) => ({ kind: "data" as const, id: gpci.id, data: gpci }))];
}

const formConfig: ObjectConfig<SaveGlobalPlanConstraintItemInput> = {
  id: { type: "value" },
  name: { type: "value" },
  lagInDays: { type: "value" },
};
